/**
 * Global "tap-during-scroll is swallowed" guard (native iOS list behavior).
 *
 * Why this exists: CrewVex runs as a `position: fixed` PWA shell with content in
 * INNER `overflow:auto` scroll containers. iOS only reliably applies its native
 * "first tap stops momentum and is absorbed (no click)" behavior to the MAIN
 * scroll view — not to inner containers in a fixed shell. So when an inner list
 * is flinging and you tap, WebKit fires the row's `onClick` (opens the detail)
 * while the list keeps gliding: "the action fires on whatever you tap." There is
 * no per-row signal that the tap was really a scroll-interrupt.
 *
 * Fix: one document-level CAPTURE-phase `click` listener. React 19 delegates its
 * events to the #root container (a descendant of `document`), so a capture
 * listener on `document` runs BEFORE React's handlers — `stopPropagation()` there
 * cancels the row's `onClick`, and `preventDefault()` cancels any default. We
 * swallow a click only when it lands inside a container that JUST scrolled (or the
 * finger dragged), within a short window.
 *
 * Touch-gated: `pointerWasTouch` is only true after a touch/coarse pointer, so a
 * mouse/trackpad click is NEVER suppressed (desktop is fully unaffected — wheel
 * scroll then click still works). iOS-PWA / touch only → can't reproduce with a
 * mouse or in jsdom gesture-wise, so the decision logic is the pure, unit-tested
 * `computeScrollTapSuppression`.
 */

/** How long after a scroll a same-container tap is treated as a scroll-interrupt. */
export const SCROLL_TAP_GUARD_MS = 150;
/** Finger travel (px) above which a press is a drag-scroll, not a tap. */
export const SCROLL_TAP_MOVE_PX = 10;
/** Default protection window for a single (non-animated) programmatic scroll. */
export const PROGRAMMATIC_SCROLL_MARK_MS = 250;

/**
 * A container we scrolled PROGRAMMATICALLY (not a user gesture) → its
 * `scroll` events must not arm this guard's own suppression, or our own
 * corrective scroll swallows the user's next tap (round 5 P0: an
 * `inlineKeyboardGuard` restore mid-tap shifted rows under the finger AND
 * got recorded here as "just scrolled," eating the row's click). Keyed by
 * element so multiple containers can be marked independently; a WeakMap
 * needs no explicit cleanup — an unmarked/removed element is simply never
 * matched again.
 */
const programmaticScrollStamps = new WeakMap();

/**
 * Stamp `el` as about to receive a scroll WE triggered, for `durationMs`.
 * Any `scroll` event this guard observes on `el` before the window expires
 * is ignored for suppression-arming purposes (see `onScroll` below) — it is
 * NOT treated as a user scroll. Pass a longer `durationMs` for a
 * `behavior:'smooth'` scroll: a smooth scroll fires MANY `scroll` events
 * over its whole animation (~300ms+), not just one, so the window must cover
 * the entire animation, not just the first event.
 */
export function markProgrammaticScroll(el, durationMs = PROGRAMMATIC_SCROLL_MARK_MS) {
  if (!el) return;
  programmaticScrollStamps.set(el, { expiresAt: Date.now() + durationMs });
}

/**
 * Pure suppression decision (no DOM) — kept separate so it's exhaustively testable.
 * Suppress iff the interaction was touch AND the click is inside the element that
 * recently scrolled AND (the finger dragged OR a scroll happened within the window).
 */
export function computeScrollTapSuppression({
  pointerWasTouch,
  touchMoved,
  msSinceScroll,
  clickInsideScrolled,
  windowMs = SCROLL_TAP_GUARD_MS,
}) {
  if (!pointerWasTouch) return false;
  if (!clickInsideScrolled) return false;
  return touchMoved || msSinceScroll < windowMs;
}

let installed = false;
let activeCleanup = null;

const noop = () => {};

/**
 * Install the guard once. Idempotent (returns the existing cleanup if already on).
 * @param {{ now?: () => number }} [opts] inject a clock for tests
 * @returns {() => void} cleanup
 */
export function installScrollTapGuard({ now = Date.now } = {}) {
  if (typeof document === 'undefined') return noop;
  if (installed) return activeCleanup || noop;

  // Fresh state each install — keeps unit tests isolated.
  let lastScrollTs = 0;
  let scrollTarget = null;
  let pointerWasTouch = false;
  let touchMoved = false;
  let startX = 0;
  let startY = 0;

  const onPointerDown = (e) => {
    if (e.pointerType === 'mouse' || e.pointerType === 'pen') {
      pointerWasTouch = false;
      return;
    }
    // touch (or an unknown coarse pointer)
    pointerWasTouch = true;
    touchMoved = false;
    startX = e.clientX || 0;
    startY = e.clientY || 0;
  };

  const onPointerMove = (e) => {
    if (!pointerWasTouch || touchMoved) return;
    const dx = (e.clientX || 0) - startX;
    const dy = (e.clientY || 0) - startY;
    if (Math.hypot(dx, dy) > SCROLL_TAP_MOVE_PX) touchMoved = true;
  };

  // Fallback for engines that fire touch but not pointer events.
  const onTouchStart = () => { pointerWasTouch = true; touchMoved = false; };

  const onScroll = (e) => {
    const target = e.target || null;
    if (target) {
      const stamp = programmaticScrollStamps.get(target);
      if (stamp) {
        if (Date.now() < stamp.expiresAt) return; // ours — never arm suppression for it
        programmaticScrollStamps.delete(target); // stale (window elapsed) — clean up and fall through
      }
    }
    lastScrollTs = now();
    scrollTarget = target;
  };

  const onClickCapture = (e) => {
    const clickInsideScrolled = !!(
      scrollTarget
      && typeof scrollTarget.contains === 'function'
      && scrollTarget.contains(e.target)
    );
    const suppress = computeScrollTapSuppression({
      pointerWasTouch,
      touchMoved,
      msSinceScroll: now() - lastScrollTs,
      clickInsideScrolled,
    });
    if (suppress) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const passiveCapture = { capture: true, passive: true };
  document.addEventListener('pointerdown', onPointerDown, passiveCapture);
  document.addEventListener('pointermove', onPointerMove, passiveCapture);
  document.addEventListener('touchstart', onTouchStart, passiveCapture);
  document.addEventListener('scroll', onScroll, passiveCapture);
  // NOT passive — we must be able to preventDefault the swallowed click.
  document.addEventListener('click', onClickCapture, { capture: true });

  installed = true;
  activeCleanup = () => {
    document.removeEventListener('pointerdown', onPointerDown, passiveCapture);
    document.removeEventListener('pointermove', onPointerMove, passiveCapture);
    document.removeEventListener('touchstart', onTouchStart, passiveCapture);
    document.removeEventListener('scroll', onScroll, passiveCapture);
    document.removeEventListener('click', onClickCapture, { capture: true });
    installed = false;
    activeCleanup = null;
  };
  return activeCleanup;
}
